import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import type { SettingsResponse } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { useLayout } from "../components/season/layout";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon } from "../components/ui/Icon";
import { Seg } from "../components/ui/Seg";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";
import { formatAt } from "../lib/zones";
import { HOST_ORDER, HOSTS, type HostKey } from "./hosts";
import { CopyValue, JoinInput, RecCard, type RecState, RecStatus, usePreviewWho } from "./parts";
import { EmailPreview, guestInbox, hostInbox, InboxMock, PreviewFit, usePreviewRoom } from "./Previews";
import { cleanDomain, looksLikeEmail, ownDomainGuess, recordName, rootOf, sendingNote, sendingOk } from "./rules";
import { BackButton, MiniSteps, Sq, type StepProps } from "./SetupView";
import { useRecordCheck } from "./useRecordCheck";
import { useSenderForm } from "./useSenderForm";

/** An example sending address under the business's own domain, or in its name, for the hint under the field. */
function exampleDomain(business: string, email: string | undefined): string {
  const own = ownDomainGuess(null, email);
  return `mail.${own ?? `${business.toLowerCase().replace(/[^a-z0-9]+/g, "") || "yourbusiness"}.com`}`;
}

// --- 2a. Where emails come from ---

/** The sending address being typed: what it says as it goes, and the save that starts the records step. */
export function useSendingAddress(data: SettingsResponse, run: StepProps["run"]) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(data.sending.domain ?? "");
  const [busy, setBusy] = useState(false);
  const next = async () => {
    const domain = cleanDomain(sub);
    if (!sendingOk(domain)) return;
    if (domain === data.sending.domain) return navigate("/setup/email/records");
    setBusy(true);
    // A different address replaces the one there: it goes first, so the email service lets go of it.
    if (data.sending.domain && !(await run(() => api.settings.turnOffSending()))) return setBusy(false);
    const ok = await run(() => api.settings.addSending({ domain }));
    setBusy(false);
    if (ok) navigate("/setup/email/records");
  };
  return { sub, setSub, busy, note: sendingNote(sub), ok: sendingOk(sub), next };
}

/** An example sending address for the hint under the field, under the business's own domain when it can be told. */
export function useExampleDomain(): string {
  const { user } = useAuth();
  return exampleDomain(user?.workspace_name ?? "", user?.email);
}

export function EmailStep({ data, run }: StepProps) {
  const { sub, setSub, busy, note, ok, next } = useSendingAddress(data, run);
  const example = useExampleDomain();
  const room = usePreviewRoom();
  const who = usePreviewWho(data, room);

  return (
    <div className="sbody">
      <Sq
        eyebrow={
          <>
            Step 2 of 4 · Your email <MiniSteps n={1} />
          </>
        }
        title="Where should your emails come from?"
        foot={
          <>
            <BackButton to="/setup/look" />
            <span className="sp" />
            <Button variant="p" size="lg" after="right" disabled={!ok || busy} onClick={() => void next()}>
              Continue
            </Button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="sub">Your sending address</label>
          <div className="ig lg">
            <input dir="auto"
              id="sub"
              className="inp lg"
              value={sub}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="none"
              inputMode="url"
              onChange={(e) => setSub(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void next();
              }}
            />
          </div>
        </div>
        <div id="subnote" aria-live="polite">
          {note.kind === "hint" ? (
            <div className="hint">Like {example}</div>
          ) : note.kind === "bad" ? (
            <div className="note warn">
              <Icon name="info" />
              <div>
                Use a web address, like <b>{example}</b>.
              </div>
            </div>
          ) : note.kind === "bare" ? (
            <div className="note warn">
              <Icon name="info" />
              <div>
                Use a part of <b>{note.domain}</b> just for sending.
                <div className="mt12">
                  <Button size="s" variant="p" onClick={() => setSub(note.suggestion)}>
                    Use {note.suggestion}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="note good">
              <Icon name="check" />
              <div>Looks good.</div>
            </div>
          )}
        </div>
      </Sq>
      <div className="sstage">
        <div className="pvbar">
          <div className="stage-cap">
            <Icon name="inbox" />{" "}
            <span>
              <b>A guest’s inbox</b>
            </span>
          </div>
        </div>
        <PreviewFit width={760}>
          <InboxMock rows={guestInbox(who, data.brand.accent)} />
        </PreviewFit>
      </div>
    </div>
  );
}

// --- 2b. The records ---

export function RecordsStep({ data, setData }: StepProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const s = data.sending;
  const verified = s.status === "verified";
  const { checking, checks, left, check } = useRecordCheck(s.domain, verified, setData);
  const [host, setHost] = useState<HostKey>(s.dns_host ?? "other");
  const [stepsOpen, setStepsOpen] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (!s.domain) return <Navigate to="/setup/email" replace />;
  const root = rootOf(s.domain);
  const n = s.records.length;
  const found = s.records.filter((r) => r.found).length;
  const stateOf = (f: boolean): RecState => (f ? "found" : checking ? "checking" : "wait");

  return (
    <div className="sbody wideL">
      <Sq
        eyebrow={
          <>
            Step 2 of 4 · Your email <MiniSteps n={2} />
          </>
        }
        title={`Add ${n} records to ${root}`}
        foot={
          <>
            <BackButton to="/setup/email" />
            <div className="row g16 sp" style={{ minWidth: 0, paddingLeft: 8 }}>
              {verified ? (
                <>
                  <RecStatus state="found" found="Email verified" />
                  <span className="sp" />
                  <Button variant="p" size="lg" after="right" onClick={() => navigate("/setup/email/test")}>
                    Continue: send a test
                  </Button>
                </>
              ) : (
                <>
                  <div className="sp row g12 recmeter">
                    <span className="recsegs" aria-hidden="true">
                      {s.records.map((r, i) => (
                        <i key={i} className={r.found ? "on" : ""} />
                      ))}
                    </span>
                    <div className="row g8 small" aria-live="polite">
                      {checking ? (
                        <>
                          <span className="spin" style={{ color: "var(--brass-ink)" }} />
                          <b>Checking each record…</b>
                        </>
                      ) : checks ? (
                        <>
                          <b>
                            {found} of {n} found
                          </b>
                          <span className="muted">
                            · checking again in <b>0:{String(Math.max(left, 0)).padStart(2, "0")}</b>
                          </span>
                        </>
                      ) : (
                        <span className="muted">
                          {found} of {n} found
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="p" size="lg" icon="refresh" disabled={checking} onClick={() => void check()}>
                    Check now
                  </Button>
                </>
              )}
            </div>
          </>
        }
      >
        <div className="recs">
          {s.records.map((r) => (
            <RecCard key={`${r.type} ${r.name}`} rec={r} root={root} state={verified ? "found" : stateOf(r.found)} />
          ))}
        </div>
        <div>
          {verified ? (
            <div className="okcard">
              <div className="big">
                <Icon name="check" />
              </div>
              <div className="sp">
                <div className="b8" style={{ fontSize: 16 }}>
                  All {n} found. Your email is verified.
                </div>
              </div>
            </div>
          ) : (
            <>
              {checks > 0 && !checking ? (
                <div className="note brass mt12">
                  <Icon name="clock" />
                  <div>This can take a few minutes. We’ll email you when it’s done.</div>
                </div>
              ) : null}
              {sentTo ? (
                <div className="note mt12">
                  <Icon name="send" />
                  <div>
                    Steps sent to <b>{sentTo}</b>.
                  </div>
                </div>
              ) : null}
              <div className="row g8 mt12">
                <span className="sp" />
                <Button
                  variant="q"
                  size="s"
                  onClick={() => {
                    toast({ text: "Saved for later", icon: "clock" });
                    navigate("/setup/address");
                  }}
                >
                  I’ll finish this later
                </Button>
              </div>
            </>
          )}
        </div>
      </Sq>
      <div className="sstage" style={{ overflow: "auto" }}>
        <HostPanel host={host} detected={s.dns_host} onHost={setHost} root={root} records={s.records} onSend={() => setStepsOpen(true)} />
      </div>
      <SendSteps open={stepsOpen} onClose={() => setStepsOpen(false)} data={data} root={root} onSent={setSentTo} />
    </div>
  );
}

/** Where to paste the records, at the business's own DNS host or any other, with someone else's way out. */
export function HostPanel({
  host,
  detected,
  onHost,
  root,
  records,
  onSend,
}: {
  host: HostKey;
  detected: HostKey | null;
  onHost: (h: HostKey) => void;
  root: string;
  records: Array<{ type: string; name: string; value: string }>;
  /** Setup's way to email the steps; the records page itself has none. */
  onSend?: () => void;
}) {
  const h = HOSTS[host];
  // Why a TXT row when there is one: it is the row every host's form has the same three boxes for.
  const sample = records.find((r) => r.type.toUpperCase() === "TXT") ?? records[0];
  return (
    <>
      <div className="row">
        <div className="h2">Where to paste them</div>
      </div>
      <div className="hosttabs mt12" role="tablist" aria-label="Where your domain is kept">
        {HOST_ORDER.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={host === k} className={cx(host === k && "on")} onClick={() => onHost(k)}>
            {HOSTS[k].label}
            {k === detected ? <span className="det"> yours</span> : null}
          </button>
        ))}
      </div>
      <ol className="hsteps mt20">
        {h.steps(root).map((step, i) => (
          <li key={i}>
            <div>{step}</div>
          </li>
        ))}
      </ol>
      {sample ? (
        <>
          <div className="mt20 lbl" style={{ marginBottom: 8 }}>
            On {host === "other" ? "your host" : h.label}
          </div>
          <div className="formmock" aria-hidden="true">
            <div className="fl">{h.fields[0]}</div>
            <div className="fl">{h.fields[1]}</div>
            <div className="fl">{h.fields[2]}</div>
            <code className="fv">{sample.type}</code>
            <code className="fv hl">{recordName(sample.name, root)}</code>
            <code className="fv hl">{sample.value}</code>
          </div>
        </>
      ) : null}
      {h.tip ? (
        <div className="note brass mt16">
          <Icon name="info" />
          <div>{h.tip.replace("{root}", root)}</div>
        </div>
      ) : null}
      {onSend ? (
        <div className="mt16 card flat" style={{ padding: 16, display: "flex", gap: 14, alignItems: "center" }}>
          <div className="sp">
            <div className="b7">Someone else runs your website?</div>
          </div>
          <Button icon="send" onClick={onSend}>
            Send the steps
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** The records, emailed to whoever runs the website, with a note in the host's own words and a page that keeps them. */
export function SendSteps({ open, onClose, data, root, onSent }: { open: boolean; onClose: () => void; data: SettingsResponse; root: string; onSent: (to: string) => void }) {
  const { user } = useAuth();
  const season = useLayout() !== "computer";
  const toast = useToast();
  const n = data.sending.records.length;
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTo("");
    setTried(false);
    setNote(`Hi, could you add ${n} records to ${root} for me? Everything is on the page in this email. Thank you! ${user?.first_name ?? ""}`.trim());
  }, [open, n, root, user?.first_name]);
  const ok = looksLikeEmail(to);
  const error = to.trim() && !ok ? "That doesn’t look like an email address yet." : tried && !ok ? "Type their email address first." : null;
  const send = async () => {
    setTried(true);
    if (!ok) return;
    setBusy(true);
    try {
      await api.settings.sendSteps({ to: to.trim(), note });
      onSent(to.trim());
      onClose();
      toast({ text: `Steps sent to ${to.trim()}`, icon: "send" });
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "We couldn’t send them just now. Try again.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };
  const link = data.sending.records_link;
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Send the steps"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" disabled={!ok || busy} onClick={() => void send()}>
            Send the steps
          </Button>
        </>
      }
    >
      <div className={cx("col", season && "st-sh")} style={{ gap: 16 }}>
        <div className="field">
          <label htmlFor="stepsTo">Their email</label>
          <input
            id="stepsTo"
            className="inp"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="name@theirwebsite.com"
            value={to}
            aria-describedby={error ? "stepsTo-err" : undefined}
            onChange={(e) => setTo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void send();
            }}
          />
          {error ? (
            <div className="hint" id="stepsTo-err" role="alert">
              <span style={{ color: "var(--bad)" }}>{error}</span>
            </div>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="stepsMsg">A note from you</label>
          <textarea dir="auto" id="stepsMsg" className="inp" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {link ? (
          <div className="field">
            <div className="lbl">Or share the link yourself</div>
            <CopyValue value={link} shown={link.replace(/^https?:\/\//, "")} title="Copy the link" />
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

// --- 2c. The test ---

export function TestStep({ data, run }: StepProps) {
  const navigate = useNavigate();
  const s = data.sending;
  const verified = s.status === "verified";
  const room = usePreviewRoom();
  const who = usePreviewWho(data, room);
  const form = useSenderForm(data, run, { fallbackName: who.business });
  const { fromName, local, reply } = form;
  const [sent, setSent] = useState<{ to: string; at: number } | null>(null);
  const [view, setView] = useState<"list" | "open">("list");
  const toast = useToast();

  if (!s.domain) return <Navigate to="/setup/email" replace />;
  const domain = s.domain;

  const send = async () => {
    const to = await form.sendTest();
    if (!to) return;
    setSent({ to, at: Date.now() });
    setView("list");
    toast({ text: "Test sent", icon: "inbox" });
  };
  const sending = form.sending;

  const at = sent ? formatAt(sent.at, { hour: "numeric", minute: "2-digit" }) : "";
  const testWho = { ...who, fromName: fromName || who.business, fromEmail: `${local}@${domain}` };
  return (
    <div className="sbody">
      <Sq
        eyebrow={
          <>
            Step 2 of 4 · Your email <MiniSteps n={3} />
          </>
        }
        title="Send yourself a test"
        foot={
          <>
            <BackButton to="/setup/email/records" />
            <span className="sp" />
            <Button variant="p" size="lg" after="right" disabled={!verified} onClick={() => navigate("/setup/address")}>
              Continue
            </Button>
          </>
        }
      >
        {verified ? null : (
          <div className="note warn">
            <Icon name="info" />
            <div>
              Records not all found yet.{" "}
              <button type="button" className="link" onClick={() => navigate("/setup/email/records")}>
                Back to the records
              </button>
            </div>
          </div>
        )}
        <div className="field">
          <label htmlFor="fn">From name</label>
          <input dir="auto" id="fn" className="inp" value={fromName} onChange={(e) => form.setFromName(e.target.value)} onBlur={() => void form.saveName()} />
        </div>
        <div className="field">
          <label htmlFor="fl">From address</label>
          <JoinInput id="fl" value={local} onChange={form.setLocal} onBlur={() => void form.saveLocal()} suffix={`@${domain}`} />
        </div>
        <div className="field">
          <label htmlFor="rt">Replies go to</label>
          <input id="rt" className="inp" type="email" inputMode="email" value={reply} onChange={(e) => form.setReply(e.target.value)} onBlur={() => void form.saveReply()} />
        </div>
        <div>
          {sent ? (
            <div className="okcard" style={{ marginTop: 0 }}>
              <div className="big">
                <Icon name="inbox" />
              </div>
              <div>
                <div className="b8">It’s on its way to your inbox.</div>
                <div className="small ink2">
                  {sent.to} · {at}
                </div>
              </div>
            </div>
          ) : (
            <Button variant="p" size="lg" icon="send" disabled={!verified || sending} style={{ alignSelf: "flex-start" }} onClick={() => void send()}>
              {sending ? "Sending…" : `Send a test to ${reply}`}
            </Button>
          )}
        </div>
      </Sq>
      <div className="sstage">
        <div className="pvbar">
          <div className="stage-cap">
            <Icon name="inbox" />{" "}
            <span>
              <b>Your inbox</b> · {reply}
            </span>
          </div>
          {sent ? (
            <Seg
              label="Show"
              value={view}
              onChange={setView}
              options={[
                { value: "list", label: "Inbox" },
                { value: "open", label: "The email" },
              ]}
            />
          ) : null}
        </div>
        <PreviewFit width={760}>
          {sent && view === "open" ? (
            <EmailPreview
              colours={data.brand}
              who={testWho}
              mail={{
                subject: `Test from ${testWho.fromName}`,
                title: `This is a test from ${testWho.fromName}`,
                body: "If you’re reading this, your address works.",
                time: at,
                test: true,
                replyTo: reply,
              }}
            />
          ) : (
            <InboxMock rows={hostInbox(testWho, data.brand.accent, !!sent)} />
          )}
        </PreviewFit>
        {sent && view === "list" ? (
          <div className="row mt20" style={{ justifyContent: "center" }}>
            <Button icon="mail" onClick={() => setView("open")}>
              Open the test email
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
